import React, { useState, useEffect, useMemo } from "react";
import { Phone, Mail, ChevronUp } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { useLocation } from "react-router-dom";

export default function RotatingContactButton() {
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [currentIcon, setCurrentIcon] = useState(0);

  const floatingIcons = useMemo(
    () => [
      <Phone size={22} className="text-brand-gold" />,
      <Mail size={22} className="text-brand-gold" />,
      <FaWhatsapp size={22} className="text-brand-gold" />,
    ],
    []
  );

  // ✅ Rotate icons
  useEffect(() => {
    if (!open) {
      const interval = setInterval(() => {
        setCurrentIcon((prev) => (prev + 1) % floatingIcons.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [open, floatingIcons]);

  // ✅ Show only after scroll & NOT in admin pages
  useEffect(() => {
    const isAdminPage = location.pathname.startsWith("/admin-dashboard");

    if (isAdminPage) {
      setShowButton(false);
      return;
    }

    const handleScroll = () => {
      if (window.scrollY > 100) {
        setShowButton(true);
      } else {
        setShowButton(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!showButton) return null;

  return (
    <div className="fixed bottom-8 right-8 z-50 flex flex-col items-end gap-4">
      {/* Contact Panel */}
      {open && (
        <div
          className="
  mb-2
  bg-white/95
  backdrop-blur-md
  shadow-2xl
  rounded-2xl
  p-4
  w-64
  border border-gray-200
"
        >
          <div className="flex flex-col gap-4">
            {/* WhatsApp */}
            <a
              href="https://wa.me/917288080999?text=Hi%20DVV%20Projects,%20I%20am%20interested%20in%20your%20properties"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 hover:bg-gray-100 p-2 rounded-lg transition"
            >
              <div className="w-10 h-10 rounded-full bg-brand-gold flex items-center justify-center text-white">
                <FaWhatsapp size={20} />
              </div>
              <span className="text-gray-700">WhatsApp</span>
            </a>

            {/* Email */}
            <a
              href="mailto:info@dvvprojects.com"
              className="flex items-center gap-3 hover:bg-gray-100 p-2 rounded-lg transition"
            >
              <div className="w-10 h-10 rounded-full bg-brand-dark flex items-center justify-center text-white">
                <Mail size={20} />
              </div>
              <span className="text-gray-700">Email Us</span>
            </a>

            {/* Phone */}
            <a
              href="tel:+917288080999"
              className="flex items-center gap-3 hover:bg-gray-100 p-2 rounded-lg transition"
            >
              <div className="w-10 h-10 rounded-full bg-brand-dark flex items-center justify-center text-white">
                <Phone size={20} />
              </div>
              <span className="text-gray-700">Call Us</span>
            </a>
          </div>
        </div>
      )}

      {/* Rotating Contact Button */}
      <button
        onClick={() => setOpen(!open)}
        className="w-12 h-12 rounded-full bg-gradient-to-r from-[#062844] to-[#062844] text-white flex items-center justify-center shadow-xl hover:scale-110 transition-transform duration-300"
        title="Contact"
      >
        {open ? "✖" : floatingIcons[currentIcon]}
      </button>

      {/* Scroll To Top */}
      <button
        onClick={scrollToTop}
        className="w-12 h-12 rounded-full bg-gradient-to-r from-[#062844] to-[#062844] text-white flex items-center justify-center shadow-xl hover:scale-110 transition-transform duration-300"
        title="Scroll to Top"
      >
        <ChevronUp size={22} />
      </button>

      {/* Powered By Logo */}
      <a
  href="https://www.charvionglobal.com/"
  target="_blank"
  rel="noopener noreferrer"
  className="relative group inline-block"
>
  <div className="p-[2px] rounded-full bg-[conic-gradient(#09558c_0deg_180deg,#f71014_180deg_360deg)]">
    <div className="w-11 h-11 rounded-full overflow-hidden flex items-center justify-center bg-white">
      <img
        src="/cgs.png"
        alt="Company Logo"
        className="w-full h-full object-cover"
      />
    </div>
  </div>

  <span
    className="
      absolute
      right-14
      top-1/2
      -translate-y-1/2
      whitespace-nowrap
      text-sm
      font-bold
      text-[#B7932D]
      opacity-0
      group-hover:opacity-100
      transition-opacity
      pointer-events-none
    "
  >
    Powered By Charvion Global Pvt. Ltd.
  </span>
</a>
    </div>
  );
}
